/**
 * B2 — coarse-grain PARALLEL layout orchestration (byte-identical).
 *
 * The dominant cost of a large interconnected pedigree is the BIDIRECTIONAL contour's
 * crossing-minimising reorder search, and it runs TWICE per layout — once for each ancestry
 * mode ('nudge' then 'side'), sequentially, keeping the better (measured: ~6s + ~10s of a
 * ~19s 214-person layout; the descent-forest run is ~1s). Those two mode runs are independent
 * pure computations, so we run them as separate tasks on a worker pool and pick the better
 * ourselves — reproducing the engine's own pick rule EXACTLY — for a ~2x wall-clock cut on
 * the search-heavy class, with the SAME final layout.
 *
 * LOCKED REOPEN (the common real case — a saved pedigree carries an establishedOrder): the
 * sync fixpoint runs `free` → (if disagrees) `seeded` → (if disagrees) `enforced`
 * SEQUENTIALLY, each a full best-of-both with the sequential mode loop inside. Here each
 * rung is decomposed into its nudge/side/descent-forest tasks, run concurrently — but the
 * rungs themselves stay GATED (seeded only dispatches if free disagrees, etc.). Do NOT
 * "optimise" this into speculative all-rungs-up-front dispatch: that was tried and it
 * REGRESSED a real 428-person open 36s → 80s — up to 9 concurrent searches oversubscribe
 * the CPU and slow the critical path more than the speculation saves (the Phase-0
 * "CPU contention inflated the timings" lesson, at browser scale).
 *
 * This module is pure orchestration over an injected `executor`:
 *   - executor.runScored(spec)  -> Promise<{ model, hop, dog, bidirUnsupported, bidirModeScore }>
 *   - executor.runFull(args)    -> Promise<model>            (a whole sync pedigree())
 * The INLINE executor (below) runs them synchronously in-process — used by tests (proving the
 * orchestration is byte-identical to sync pedigree()) and as the jsdom/no-worker fallback. The
 * WORKER-POOL executor (layoutPool.js) runs runScored tasks across Web Workers.
 *
 * Orientation-seed (auto-arrange) / oversized / non-contour cases delegate to a single full
 * sync pedigree() task (executor.runFull), so they are exactly as today.
 */
import { config } from './config.js';
import { PedigreeFamily } from './Family.js';
import {
  pedigree,
  buildRenderModel,
  runOneLayout,
  hasBothAncestriedCouple,
  hopoverScore,
  doglegScore,
  modelScore,
  rankedSequenceAgrees,
  destraddleScore,
  destraddleCandidateOrders,
  wedgeParkCandidateOrders,
  spouseStretchScore
} from './layout.js';

// CROSS-GEN PRE-CHECK. A family with a CROSS-GENERATIONAL couple (partners on different
// rows) is `bidirUnsupported`: the engine's bidirectional branch falls back to the
// descent-forest for ALL ancestry modes, so the nudge/side/df tasks are byte-identical —
// and dispatching three of them triples the work for zero benefit (measured on G-1-01456:
// three identical ~45s tasks, each ~3× contention-inflated; solo ≈ 15s). Cross-gen is the
// one bidirUnsupported condition computable cheaply up-front (containment/diverged need the
// full seed): mirror the engine's vertical preamble (generation assignment + component
// alignment set rowDepth; executeLayoutV2 then copies rowDepth → virtualY) and compare
// spouse rows. ~10-30ms for a 428-person family. When true, bestOfBothParallel collapses to
// the single descent-forest task — exactly the model the ladder returns today when the df
// task reports bidirUnsupported (B1b), just without paying for the two clones.
function precheckCrossGenCouple(family, familyId) {
  try {
    const pf = new PedigreeFamily(familyId);
    family.forEach((p) => pf.addPerson(p));
    pf.calculateGenerationsFromRelationships();
    pf.alignDisconnectedComponentsToTop();
    return pf.persons.some((p) =>
      (p.spouseIds || []).some((sid) => {
        const sp = pf.getPersonById(sid);
        return sp && (sp.rowDepth ?? 0) !== (p.rowDepth ?? 0);
      })
    );
  } catch (e) {
    return false; // never block the normal path on a pre-check failure
  }
}

// Run ONE scored layout task. Shared by the inline executor and the worker so the computation
// (and therefore the score) is identical wherever it runs. `locked` / `seedOrder`+`seeded`
// reproduce the fixpoint rungs' layoutOnce environments (enforced / seeded respectively).
export function runScoredTask(
  {
    family,
    familyId,
    customPositions,
    bidir,
    ancModeForce,
    locked = null,
    seedOrder = null,
    seeded = false
  },
  // Optional: called with the engine's bidirUnsupported verdict as soon as the engine phase
  // finishes (before the refine) — the worker forwards it as an interim message so the
  // orchestrator can start/skip the other mode tasks without waiting for the refine.
  onVerdict = null
) {
  const phases = {};
  const pf = runOneLayout({
    family,
    familyId,
    customPositions,
    locked,
    seedOrder,
    seeded,
    bidir,
    ancModeForce,
    _phases: phases,
    _onEngineDone: onVerdict
      ? (enginePf) => onVerdict(!!enginePf._bidirUnsupportedV3)
      : null
  });
  const model = buildRenderModel(pf, customPositions);
  // Telemetry only (never used for decisions): seed-stage split + which detectors fired,
  // and the refine's trial-evaluation counters.
  if (pf._seedStagesV3) phases.seed = pf._seedStagesV3;
  if (pf._refineStatsV3) phases.refineStats = pf._refineStatsV3;
  return {
    model,
    hop: hopoverScore(model, pf), // { cross, over, sum }
    dog: doglegScore(model),
    bidirUnsupported: !!pf._bidirUnsupportedV3,
    bidirModeScore: pf._bidirModeScoreV3,
    // Telemetry only (never used for decisions): engine-vs-refine wall-clock split.
    phases
  };
}

// Pick the better ancestry mode. The engine's internal loop keeps a mode only when its score
// is STRICTLY lower than the incumbent's, iterating nudge then side — so nudge wins ties. We
// reproduce that exactly: nudge unless side is strictly lower.
function pickMode(nudge, side) {
  return side.bidirModeScore < nudge.bidirModeScore ? side : nudge;
}

// The best-of-both ladder (identical to pedigree()'s inline `biBetter`): fewer hop-overs, then
// fewer marriage-over-node, then fewer dog-legs beyond a 1px deadband.
function biBeatsDf(df, bi) {
  const DL_EPS = 1;
  return (
    bi.hop.sum < df.hop.sum ||
    (bi.hop.sum === df.hop.sum && bi.hop.over < df.hop.over) ||
    (bi.hop.sum === df.hop.sum &&
      bi.hop.over === df.hop.over &&
      bi.dog + DL_EPS < df.dog)
  );
}

// ONE layout pass decomposed across the executor: the two ancestry modes in parallel (plus
// the descent-forest for a both-ancestried family), reassembled with the engine's own pick
// rules. This is the parallel equivalent of a single `layoutOnce`-based pedigree() core —
// reused for the unlocked layout and for each fixpoint rung (locked / seeded variants).
async function bestOfBothParallel(
  base,
  executor,
  rung = {},
  name = 'layout',
  crossGen = false
) {
  const common = { ...base, ...rung };
  // Cross-gen family (pre-checked): every mode task collapses to the identical
  // descent-forest fallback inside the engine — run it ONCE. Same model the ladder below
  // returns for a bidir-unsupported df result (B1b), without paying for two clones.
  if (crossGen) {
    const df = await executor.runScored({
      ...common,
      bidir: false,
      ancModeForce: null,
      label: `${name}:df`
    });
    return df.model;
  }
  const bothAnc = hasBothAncestriedCouple(base.family);
  const nudgeSpec = {
    ...common,
    bidir: true,
    ancModeForce: 'nudge',
    label: `${name}:nudge`
  };
  const sideSpec = {
    ...common,
    bidir: true,
    ancModeForce: 'side',
    label: `${name}:side`
  };
  const dfSpec = {
    ...common,
    bidir: false,
    ancModeForce: null,
    label: `${name}:df`
  };

  // VERDICT-FIRST DISPATCH. Containment / diverged families are bidirUnsupported like
  // cross-gen ones, but only the engine's own seed can tell — the pre-check above can't.
  // For such a family every mode task is byte-identical (all fall back to the descent
  // forest), so dispatching three of them triples the work and contention for nothing
  // (measured on G-1-01456: 3 identical ~29s tasks; one solo task is ~2× faster than each).
  // So: run the FIRST task alone; the worker reports the engine's verdict as an interim
  // message the moment the engine phase finishes (before the refine). Unsupported → this
  // task IS the layout, nothing else dispatches. Supported → the remaining tasks dispatch
  // immediately and overlap the first task's refine (for a small supported family the
  // verdict lands within ~a second, so the lost overlap is minor and the reduced contention
  // pays most of it back). Results and the pick ladder are unchanged — byte-identical.
  const first = executor.runScoredWithVerdict(bothAnc ? dfSpec : nudgeSpec);
  const unsupported = await first.verdict;
  if (unsupported) return (await first.result).model;

  if (bothAnc) {
    const [nudge, side] = await Promise.all([
      executor.runScored(nudgeSpec),
      executor.runScored(sideSpec)
    ]);
    const df = await first.result;
    // B1b ladder (unchanged): df wins unless the better bidir mode strictly beats it.
    if (df.bidirUnsupported) return df.model;
    const bi = pickMode(nudge, side);
    return biBeatsDf(df, bi) ? bi.model : df.model;
  }
  // Single bidir layout: the engine would pick the better mode internally; we do it here.
  const side = await executor.runScored(sideSpec);
  const nudge = await first.result;
  return pickMode(nudge, side).model;
}

// The de-straddle reseed loop (identical to pedigree()'s inline `maybeDestraddle`, unlocked
// branch). Candidates are INDEPENDENT (each reseeds from `model`, not from the running best),
// so they run in parallel; the Pareto adoption is then applied in canonical candidate order.
async function applyDestraddle(model, base, executor) {
  if (!config.ENABLE_LAYOUT_V2 || !config.ENABLE_CONTOUR_MERGE_XASSIGN)
    return model;
  if (config.ENABLE_DESTRADDLE_RESEED === false) return model;
  // Byte-identical mirror of layout.js's maybeDestraddle (parity suites pin
  // it): straddle candidates with the legacy gate + WEDGE-PARK candidates
  // with their own strictly-gated dog rung.
  const specs = destraddleCandidateOrders(model).map((order) => ({
    order,
    kind: 'straddle'
  }));
  if (config.ENABLE_WEDGE_PARK_RESEED !== false) {
    for (const order of wedgeParkCandidateOrders(model))
      specs.push({ order, kind: 'wedgePark' });
  }
  specs.splice(4);
  if (!specs.length) return model;
  const personWidth = (m) => {
    const xs = m.nodes
      .filter((n) => n.kind === config.NODE_TYPE.NODE)
      .map((n) => n.x);
    return xs.length ? Math.max(...xs) - Math.min(...xs) : 0;
  };
  const cands = await Promise.all(
    specs.map(({ order }, i) =>
      executor.runFull(
        {
          ...base,
          establishedOrder: order,
          _noDestraddle: true,
          _freeModel: model
        },
        `destraddle:${i}`
      )
    )
  );
  let bestModel = model;
  let bestScore = destraddleScore(model);
  let adopted = false;
  for (let i = 0; i < cands.length; i++) {
    const cand = cands[i];
    const kind = specs[i].kind;
    const cs = destraddleScore(cand);
    const better =
      kind === 'straddle' &&
      cs.cross < bestScore.cross &&
      cs.over <= bestScore.over &&
      cs.lanes <= bestScore.lanes;
    const tieBreak =
      kind === 'straddle' &&
      adopted &&
      cs.cross === bestScore.cross &&
      cs.over === bestScore.over &&
      cs.lanes === bestScore.lanes &&
      doglegScore(cand) < doglegScore(bestModel) - 1e-6;
    const DLW = config.NODE_GAP || 800;
    const wedgeParkBetter =
      kind === 'wedgePark' &&
      cs.cross <= bestScore.cross &&
      cs.over <= bestScore.over &&
      cs.lanes <= bestScore.lanes &&
      spouseStretchScore(cand) <= spouseStretchScore(bestModel) + 2 * DLW &&
      personWidth(cand) <= personWidth(bestModel) + 2 * DLW &&
      doglegScore(cand) < doglegScore(bestModel) - 0.25 * DLW;
    if (better || tieBreak || wedgeParkBetter) {
      bestModel = cand;
      bestScore = cs;
      adopted = true;
    }
  }
  return bestModel;
}

// The full unlocked layout: parallel best-of-both, then the de-straddle reseed. This is what
// the sync path computes for a plain open AND for the locked fixpoint's `free` rung (which is
// an unlocked layout over the same inputs).
async function unlockedParallel(
  base,
  executor,
  name = 'free',
  crossGen = false
) {
  const winner = await bestOfBothParallel(base, executor, {}, name, crossGen);
  return applyDestraddle(winner, base, executor);
}

// Which calls the parallel orchestration handles itself. Orientation-seed (auto-arrange),
// oversized (runaway guard) and non-contour configurations delegate to a single sync task.
function isParallelisable({ family, orientationSeed }) {
  const tooBig = family.length > (config.MAX_LAYOUT_SEARCH_SIZE ?? Infinity);
  return (
    !(orientationSeed && orientationSeed.size) &&
    !tooBig &&
    config.ENABLE_LAYOUT_V2 &&
    config.ENABLE_CONTOUR_MERGE_XASSIGN &&
    config.ENABLE_BIDIRECTIONAL_CONTOUR
  );
}

/**
 * Parallel pedigree layout. Returns the SAME model as sync `pedigree(args)` — verified by
 * loadPerfParallelParity.test.js across the corpus — computed with the independent layout
 * passes run concurrently on the executor.
 */
export async function pedigreeParallel(args, executor) {
  if (!isParallelisable(args)) {
    // Not a parallel case — one full sync layout, exactly as today.
    return executor.runFull(args, 'delegate');
  }
  const { family, familyId, customPositions, establishedOrder } = args;
  const base = { family, familyId, customPositions };
  // One cheap vertical pass decides whether the mode decomposition is worth dispatching at
  // all (see precheckCrossGenCouple). Computed once per layout, reused by every rung.
  const crossGen = precheckCrossGenCouple(family, familyId);

  // LOCKED FIXPOINT (saved pedigree). Mirrors pedigree()'s establishedOrder branch exactly.
  // RUNGS ARE GATED, NOT SPECULATED: only the active rung's tasks are in flight, so at most
  // nudge+side+df (2 heavy + 1 light) compute concurrently. A speculative version (all three
  // rungs dispatched up-front, up to 9 concurrent searches) REGRESSED F-1-01456 36s → 80s
  // in-app: the extra searches oversubscribe the CPU and slow the critical path (the free
  // rung's slowest mode) 2-3× — the Phase-0 contention lesson at browser scale. The common
  // agree-case (a reopened, unchanged pedigree) never runs the seeded/enforced rungs at all,
  // exactly like sync.
  if (establishedOrder && config.ENABLE_ESTABLISHED_ORDER) {
    const order = establishedOrder;
    // Rung 1 — the fixpoint fast-path: the from-scratch settle reproduces the saved order.
    const free = await unlockedParallel(base, executor, 'free', crossGen);
    // ARRANGE BEACON grading — byte-identical mirror of the sync ladder in
    // layout.js's gradeArrangeBeacon (loadPerfParallelParity pins arrangeHint):
    // reference = the pin-free unlocked optimum (what Auto-arrange produces);
    // without pins that is `free` itself, and free === returned on the
    // fast-path ⇒ no hint. Applied to BOTH return paths.
    const gradeArrangeBeacon = async (returned) => {
      if (!config.ENABLE_ARRANGE_BEACON) return returned;
      const havePins = base.customPositions && base.customPositions.size > 0;
      const reference = havePins
        ? await unlockedParallel(
            { ...base, customPositions: null },
            executor,
            'beacon-ref',
            crossGen
          )
        : free;
      if (reference === returned) return returned;
      const fS = modelScore(reference);
      const eS = modelScore(returned);
      const DL = config.NODE_GAP || 800;
      const SDB = 2 * (config.NODE_GAP || 800);
      if (
        fS.sum < eS.sum ||
        (fS.sum === eS.sum && fS.over < eS.over) ||
        (fS.sum === eS.sum &&
          fS.over === eS.over &&
          fS.stretch < eS.stretch - SDB) ||
        (fS.sum === eS.sum &&
          fS.over === eS.over &&
          Math.abs(fS.stretch - eS.stretch) <= SDB &&
          doglegScore(reference) < doglegScore(returned) - DL)
      ) {
        returned.arrangeHint = true;
      }
      return returned;
    };
    if (rankedSequenceAgrees(free.nodes, order))
      return gradeArrangeBeacon(free);
    // Rung 2 — the seeded pipeline still reproduces the saved sequence.
    const seeded = await bestOfBothParallel(
      base,
      executor,
      { seedOrder: order, seeded: true },
      'seeded',
      crossGen
    );
    const enforced = rankedSequenceAgrees(seeded.nodes, order)
      ? seeded
      : await bestOfBothParallel(
          base,
          executor,
          { locked: order },
          'enforced',
          crossGen
        );
    // ARRANGE BEACON — grade the enforced result against the canonical free optimum with the
    // exact sync ladder (crossings+overs → overs → stretch beyond a 2-slot deadband → dog-leg
    // beyond one grid unit).
    return gradeArrangeBeacon(enforced);
  }

  return unlockedParallel(base, executor, 'main', crossGen);
}

// Synchronous in-process executor — the correctness reference and the no-worker fallback.
export const inlineExecutor = {
  runScored: (spec) => Promise.resolve(runScoredTask(spec)),
  // Synchronous verdict-first: the task runs to completion inline, then both promises
  // resolve (verdict first from the engine callback, else from the result's flag) — the
  // orchestrator's await sequence behaves identically, just without overlap.
  runScoredWithVerdict: (spec) => {
    let verdictVal = null;
    const result = runScoredTask(spec, (u) => {
      verdictVal = u;
    });
    return {
      verdict: Promise.resolve(verdictVal ?? !!result.bidirUnsupported),
      result: Promise.resolve(result)
    };
  },
  runFull: (args) => Promise.resolve(pedigree(args))
};
